import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Grid,
} from "@mui/material";
import { useAppSelector } from "../store/hooks";
import { selectUser, selectIsLoggedIn } from "../store/userSlice";
import { useAuth } from "../AuthContext";
import API_BASE_URL from "../apiConfig";
import MKBox from "../components/MKBox";
import MKButton from "../components/MKButton";
import MKTypography from "../components/MKTypography";
import SignInModal from "../components/auth/SignInModal";

function CreateClubPage() {
  const user = useAppSelector(selectUser);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    club_name: "",
    club_handle: "",
    club_description: "",
    club_category: "",
    contact_email: "",
    contact_phone: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [checkingHandle, setCheckingHandle] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear handle availability when handle changes
    if (field === 'club_handle') {
      setHandleAvailable(null);
    }
  };



  const checkHandleAvailability = async (handle) => {
    if (!handle || handle.length < 3) {
      setHandleAvailable(null);
      return;
    }

    setCheckingHandle(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/clubs/check-handle/${handle}`);
      const data = await response.json();
      setHandleAvailable(data.available);
    } catch (error) {
      console.error('Error checking handle:', error);
      setHandleAvailable(null);
    } finally {
      setCheckingHandle(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!formData.club_name.trim()) {
      setError("Club name is required");
      return;
    }

    if (!formData.club_handle.trim()) {
      setError("Club handle is required");
      return;
    }

    if (formData.club_handle.trim().length < 3) {
      setError("Club handle must be at least 3 characters long");
      return;
    }

    if (!formData.club_description.trim()) {
      setError("Club description is required");
      return;
    }

    if (!formData.club_category.trim()) {
      setError("Club category is required");
      return;
    }

    if (handleAvailable === false) {
      setError("This handle is already taken. Please choose a different one.");
      return;
    }

    if (handleAvailable === null) {
      setError("Please wait for handle availability check to complete");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/new-club-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit club request");
      }

      setSuccess("Club creation request submitted successfully! We'll review your request and get back to you soon.");
      setFormData({
        club_name: "",
        club_handle: "",
        club_description: "",
        club_category: "",
        contact_email: "",
        contact_phone: ""
      });
      setHandleAvailable(null);
    } catch (error) {
      console.error("Submit error:", error);
      setError(error.message || "Failed to submit club request");
    } finally {
      setLoading(false);
    }
  };

  const getHandleStatus = () => {
    if (checkingHandle) return { text: "Checking availability...", color: "info" };
    if (handleAvailable === true) return { text: "✓ Handle available", color: "success" };
    if (handleAvailable === false) return { text: "✗ Handle taken", color: "error" };
    return { text: "", color: "text.secondary" };
  };

  const handleStatus = getHandleStatus();

  const handleSignIn = async (credentials) => {
    try {
      await login(credentials);
      setShowSignInModal(false);
    } catch (error) {
      console.error("Sign in error:", error);
      // Re-throw the error so SignInModal can handle it
      throw error;
    }
  };

  // Show login modal for non-logged-in users
  if (!isLoggedIn) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
        <MKTypography variant="h3" fontWeight="bold" textAlign="center" mb={4}>
          Request Club Creation
        </MKTypography>

        <Card>
          <CardContent>
            <MKTypography variant="h5" fontWeight="bold" mb={2}>
              Sign In Required
            </MKTypography>
            
            <MKTypography variant="body2" color="text.secondary" mb={3}>
              You need to be signed in to request a club creation. Please sign in to continue.
            </MKTypography>

            <MKBox sx={{ display: "flex", justifyContent: "center" }}>
              <MKButton
                variant="contained"
                onClick={() => setShowSignInModal(true)}
                sx={{
                  bgcolor: "#000000",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  "&:hover": {
                    bgcolor: "#333333"
                  }
                }}
              >
                Sign In to Continue
              </MKButton>
            </MKBox>
          </CardContent>
        </Card>

        <SignInModal
          open={showSignInModal}
          onClose={() => setShowSignInModal(false)}
          onSignIn={handleSignIn}
          onSwitchToSignUp={() => setShowSignInModal(false)}
          onSwitchToForgotPassword={() => setShowSignInModal(false)}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <MKTypography variant="h3" fontWeight="bold" textAlign="center" mb={4} sx={{ color: '#e91e63' }}>
        Request Club Creation
      </MKTypography>

      <Card>
        <CardContent>
          <MKTypography variant="h5" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
            Club Information
          </MKTypography>
          
          <MKTypography variant="body2" color="text.secondary" mb={3}>
            Fill out the form below to request a new club. Your request will be reviewed and you'll be notified via email once approved.
          </MKTypography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Club Name"
                  value={formData.club_name}
                  onChange={(e) => handleInputChange('club_name', e.target.value)}
                  placeholder="Enter your club name"
                  helperText="This will be the display name of your club"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Club Handle"
                  value={formData.club_handle}
                  onChange={(e) => {
                    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                    handleInputChange('club_handle', value);
                    if (value.length >= 3) {
                      checkHandleAvailability(value);
                    }
                  }}
                  placeholder="e.g., my-gaming-club"
                  helperText={`${formData.club_handle.length}/30 characters. Use only letters, numbers, dashes, and underscores. ${handleStatus.text}`}
                  required
                  error={handleAvailable === false}
                  InputProps={{
                    endAdornment: checkingHandle && <CircularProgress size={20} />
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Club Category"
                  value={formData.club_category}
                  onChange={(e) => handleInputChange('club_category', e.target.value)}
                  placeholder="e.g., FPS Games, Strategy Games, Board Games"
                  helperText="What type of games or activities will your club focus on?"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Club Description"
                  value={formData.club_description}
                  onChange={(e) => handleInputChange('club_description', e.target.value)}
                  placeholder="Briefly describe your club, what games you play, meeting times, etc."
                  helperText={`${formData.club_description.length}/500 characters`}
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 500 }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  value={formData.contact_email}
                  onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  placeholder="club@example.com"
                  helperText="Primary contact email for the club"
                  type="email"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Phone (Optional)"
                  value={formData.contact_phone}
                  onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  helperText="Primary contact phone for the club"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <MKBox sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <MKButton
                type="submit"
                variant="contained"
                disabled={loading || handleAvailable === false}
                sx={{
                  bgcolor: "#000000",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#333333"
                  }
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Submit Request"}
              </MKButton>
            </MKBox>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <MKTypography variant="h6" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
            What happens next?
          </MKTypography>
          <MKTypography variant="body2" color="text.secondary">
            <ul>
              <li>Your request will be reviewed by our team</li>
              <li>We'll check the handle availability and approve the request</li>
              <li>You'll receive an email confirmation once approved</li>
              <li>Your club will be created and you'll be set as the owner</li>
              <li>You can then start customizing your club page</li>
            </ul>
          </MKTypography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default CreateClubPage; 